import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  TablePagination,
  alpha,
} from '@mui/material';
import { useState } from 'react';
import { OptionsCluster } from '@/types';

interface OptionsFlowTableProps {
  data: OptionsCluster[];
  isLoading?: boolean;
}

// Format premium to readable string (e.g., $1.2M, $500K)
function formatPremium(premium: number): string {
  if (premium >= 1_000_000) {
    return `$${(premium / 1_000_000).toFixed(1)}M`;
  }
  if (premium >= 1_000) {
    return `$${(premium / 1_000).toFixed(0)}K`;
  }
  return `$${premium.toFixed(0)}`;
}

// Format number with thousand separators
function formatNumber(num: number): string {
  return num.toLocaleString();
}

// Format price
function formatPrice(price: number | null): string {
  if (price === null) return '-';
  return `$${price.toFixed(2)}`;
}

// Format expiry date
function formatExpiry(expiry: string | null): string {
  if (!expiry) return '-';
  // expiry is in format YYYY-MM-DD or similar
  return expiry;
}

export default function OptionsFlowTable({ data, isLoading }: OptionsFlowTableProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (isLoading) {
    return null; // Loading handled by parent
  }

  if (data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
        <Typography>暂无数据</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Ticker</TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Expiry</TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Strike</TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>C/P</TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Size</TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Price</TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Premium</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.underlying}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatExpiry(row.expiry)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      ${row.strike?.toFixed(0) || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.call_put === 'C' ? 'Call' : row.call_put === 'P' ? 'Put' : '-'}
                      size="small"
                      sx={{
                        bgcolor: row.call_put === 'C'
                          ? (theme) => alpha(theme.palette.success.main, 0.15)
                          : row.call_put === 'P'
                            ? (theme) => alpha(theme.palette.secondary.main, 0.15)
                            : 'transparent',
                        color: row.call_put === 'C'
                          ? 'success.main'
                          : row.call_put === 'P'
                            ? 'secondary.main'
                            : 'text.secondary',
                        fontWeight: 500,
                        minWidth: 48,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatNumber(row.total_size)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatPrice(row.vwap)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                      {formatPremium(row.total_premium)}
                    </Typography>
                  </TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="每页显示"
      />
    </Paper>
  );
}
